import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogTitle, DialogContent, Dialog, Button, DialogActions, Checkbox, FormControlLabel,
} from '@mui/material';
import useToastContext from '@/hooks/useToastContext';
import styles from './DialogFieldMaintenance.module.scss';
import { FieldOption } from '../TabFieldsMaintenance/TabFieldsMaintenance';
import InputText from '../UI/InputText/InputText';

interface DialogFieldMaintenanceProps {
  close: () => void;
  title: string;
  onSave: (fieldOption) => void;
  currentFields: FieldOption[];
}

const DialogFieldMaintenance: React.FC<DialogFieldMaintenanceProps> = (props) => {
  const { close, title, onSave, currentFields } = props;
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const [sending, setSending] = useState(false);

  const COLUMNS_DEFAULTS_FILTERS = {
    EN: '',
    DE: '',
    active: false,
  };

  const [columnFilters, setColumnFilters] = useState(COLUMNS_DEFAULTS_FILTERS);

  const validateFields = () => columnFilters.DE.length > 1 && columnFilters.EN.length > 1;

  const canAdd = validateFields() || sending;

  const handleEditField = (inputName, textInput) => {
    setColumnFilters({ ...columnFilters, [inputName]: textInput });
  };

  const handleOnSave = () => {
    setSending(true);
    const newKey = `${columnFilters.EN.toUpperCase().trim().replace(/[^\w\s]/gi, '').replace(/ /g, '_')}_GENERATED`;
    const optionExists = currentFields.some(({ key, EN, DE }) => key === newKey || EN === columnFilters.EN || DE === columnFilters.DE);

    if (optionExists) {
      addToast(t('administration.fieldsMaintenance.errorUniqueOption'), 'ERROR');

      return;
    }

    const toSend = {
      ...columnFilters,
      key: newKey,
      lastBoolModified: columnFilters.active,
    };

    onSave(toSend);
  };

  return (
    <Dialog
      open
      maxWidth="md"
      onClose={close}
      aria-labelledby="form-dialog-title-fieldmaintenance"
    >
      <DialogTitle id="form-dialog-title-fieldmaintenance">{title}</DialogTitle>
      <DialogContent className={styles.Content}>
        <div className={styles.DialogFields}>
          <div className={styles.PageContent}>
            <div className={styles.Fields}>
              <InputText
                id="input-fieldmaintenance-nameEN"
                value={columnFilters.EN}
                isMandatory
                label={t('administration.fieldsMaintenance.content', { lng: 'en' })}
                handleChange={(ev) => handleEditField('EN', ev.target.value)}
              />
              <InputText
                id="input-fieldmaintenance-nameDE"
                value={columnFilters.DE}
                isMandatory
                label={t('administration.fieldsMaintenance.content', { lng: 'de' })}
                handleChange={(ev) => handleEditField('DE', ev.target.value)}
              />
              <FormControlLabel
                id="input-fieldmaintenance-active"
                checked={columnFilters.active}
                control={<Checkbox data-testid="input-fieldmaintenance-active" color="primary" onChange={(event) => { handleEditField('active', event.target.checked); }} />}
                label={t('administration.fieldsMaintenance.active')}
                labelPlacement="top"
              />
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleOnSave} disabled={!canAdd}>
          {t('dialog.save')}
        </Button>
        <Button color="primary" onClick={close}>{t('dialog.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogFieldMaintenance;
